import { ColumnHelper } from "@tanstack/react-table";
import AppContext from "PFApp/app_context";
import PageBox from "PFComponents/box/page_box";
import Table from "PFComponents/table/table";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import fetchCurrentAccount from "PFCore/services/accounts/current_account";
import SearchIcon from "PFIcons/search.svg";
import { CURRENT_ACCOUNT_ACTIONS } from "PFReducers/current_account_reducer";
import { Feature } from "PFTypes/features";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import SaasAdminHeader from "../../shared/saas_admin_header";
import { ActionCell } from "./cells/action_cell";
import { DefaultCell } from "./cells/default_cell";
import { DependsOnCell } from "./cells/depends_on_cell";
import { StandardCell } from "./cells/standard_cell";
import css from "./features_list_page.scss";

type FeaturesListPageProps = {
  features: Feature[];
  toggleFeature: (feature: Feature) => void;
  accountName: string;
  currentPage: number;
  setCurrentPage: any;
  totalPages: number;
};

const FeaturesListPage = ({
  features,
  toggleFeature,
  accountName,
  currentPage,
  setCurrentPage,
  totalPages
}: FeaturesListPageProps): JSX.Element => {
  const { t } = useTranslation("saasAdmin");

  const [filteredFeatures, setFilteredFeatures] = useState(features);
  const [filterValue, setFilterValue] = useState<string | undefined>(undefined);
  const { dispatch } = useContext(AppContext);

  useEffect(() => {
    setFilteredFeatures(features);
    handleFilterFeatures(filterValue);
  }, [features]);

  const refreshAccount = async () => {
    const account = await fetchCurrentAccount();
    if (account) {
      dispatch({
        type: CURRENT_ACCOUNT_ACTIONS.CURRENT_ACCOUNT_READY,
        payload: account
      });
    }
  };

  /* @ts-ignore */
  useEffect(() => () => refreshAccount(), []);

  const handleFilterFeatures = (value: string | undefined) => {
    if (value !== "" && value !== undefined) {
      const filtered = features.filter((feature) => feature.name.includes(value));
      setFilteredFeatures(filtered);
    } else {
      setFilteredFeatures(features);
    }
    setFilterValue(value);
  };

  const columns = (columnHelper: ColumnHelper<Feature>) => [
    columnHelper.accessor((row) => row.name, {
      id: "name",
      header: t("accounts.features.columns.name"),
      cell: (info) => <StandardCell value={info.getValue()} />,
      enableSorting: false,
      size: 300
    }),
    columnHelper.accessor((row) => row.description, {
      id: "description",
      header: t("accounts.features.columns.description"),
      cell: (info) => <StandardCell value={info.getValue()} />,
      enableSorting: false
    }),
    columnHelper.accessor((row) => row.dependsOn, {
      id: "dependsOn",
      header: t("accounts.features.columns.dependsOn"),
      cell: (info) => <DependsOnCell value={info.getValue()} />,
      enableSorting: false,
      size: 120
    }),
    columnHelper.accessor((row) => row.default, {
      id: "default",
      header: t("accounts.features.columns.default"),
      cell: (info) => <DefaultCell attribute={info.getValue()} />,
      enableSorting: false,
      size: 120
    }),
    columnHelper.accessor((row) => row, {
      id: "actions",
      header: t("accounts.features.columns.actions"),
      cell: (info) => <ActionCell feature={info.getValue()} features={features} onClick={toggleFeature} />,
      enableSorting: false,
      size: 120
    })
  ];

  return (
    <div>
      <div className={css.headerActions}>
        <SaasAdminHeader
          title={t("accounts.features.title")}
          subTitle={`(Showing features for ${accountName})`}
          fixedWidth={false}
        />
        <InputFieldSet
          label={t("accounts.features.filterFeatures")}
          icon={<SearchIcon width={20} height={20} />}
          onChange={handleFilterFeatures}
        />
      </div>
      <PageBox className={css.featuresPage}>
        <Table<Feature>
          tableData={filteredFeatures}
          generateColumns={columns}
          paginationOptions={{
            currentPage,
            onClick: setCurrentPage,
            totalPages
          }}
          compact
        />
      </PageBox>
    </div>
  );
};

export default FeaturesListPage;
